import React, { useState } from 'react';
import axios from 'axios';
import DecisionPanel from './DecisionPanel';

function formatPct(val) {
  if (val == null) return '—';
  return (parseFloat(val) * 100).toFixed(2) + '%';
}

function regimeBadge(flag) {
  if (!flag) return null;
  const labels = { UPTREND: 'Uptrend', SIDEWAYS: 'Sideways', DOWNTREND: 'Downtrend', VOLATILE: 'Volatile' };
  return <span className={`regime regime-${flag}`}>{labels[flag] || flag}</span>;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function Scanner() {
  const [results, setResults] = useState(null);
  const [ruleSet, setRuleSet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scannedAt, setScannedAt] = useState(null);
  const [selected, setSelected] = useState(null);

  async function runScan() {
    setLoading(true);
    setError(null);
    setResults(null);
    setSelected(null);
    try {
      const [scanRes, rsRes] = await Promise.all([
        axios.get('/api/scanner'),
        axios.get('/api/rule-set'),
      ]);
      setResults(scanRes.data);
      setRuleSet(rsRes.data);
      setScannedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  const now = new Date().toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });

  return (
    <>
      <div className="topbar">
        <span className="page-title">Opportunity scanner</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span className="timestamp">{now}</span>
          <button className="btn-add-trade" onClick={runScan} disabled={loading}>
            {loading ? 'Scanning...' : '⟳ Run scan'}
          </button>
        </div>
      </div>

      {!results && !loading && !error && (
        <div className="scanner-empty">
          <div className="scanner-empty-icon">⟳</div>
          <p>Press <strong>Run scan</strong> to scan your watchlist for opportunities.</p>
          <p className="scanner-empty-sub">Scans your watchlist against your active rule set and ranks results by capital efficiency and yield.</p>
        </div>
      )}

      {loading && (
        <div className="scanner-empty">
          <div className="scanner-empty-icon" style={{ animation: 'spin 1.5s linear infinite', display: 'inline-block' }}>⟳</div>
          <p>Scanning watchlist...</p>
          <p className="scanner-empty-sub">Pulling live options chains from Tradier. This takes 20–40 seconds.</p>
        </div>
      )}

      {error && (
        <div className="form-error" style={{ marginTop: 24 }}>
          Scanner error: {error}
        </div>
      )}

      {results && (
        <>
          <div className="scanner-meta">
            {results.noAction ? (
              <div className="no-action-banner">
                <div className="no-action-title">No action recommended</div>
                <div className="no-action-reason">{results.reason}</div>
              </div>
            ) : (
              <div className="scanner-summary">
                <span>{results.opportunities.length} opportunit{results.opportunities.length === 1 ? 'y' : 'ies'} found</span>
                <span className="scanner-summary-sep">·</span>
                <span>{results.contractsEvaluated} contracts passed filters</span>
                <span className="scanner-summary-sep">·</span>
                <span>Scanned at {scannedAt?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
              </div>
            )}
          </div>

          {!results.noAction && results.opportunities.length > 0 && (
            <>
              <div className="table-header" style={{ gridTemplateColumns: '80px 100px 80px 90px 90px 100px 110px 110px 100px' }}>
                <span className="th">Ticker</span>
                <span className="th">Expiration</span>
                <span className="th">Strike</span>
                <span className="th">Delta</span>
                <span className="th">IV</span>
                <span className="th">Wkly yield</span>
                <span className="th">Fill-adj premium</span>
                <span className="th">Regime</span>
                <span className="th"></span>
              </div>

              {results.opportunities.map((o, i) => (
                <div
                  key={i}
                  className="position-row"
                  style={{
                    gridTemplateColumns: '80px 100px 80px 90px 90px 100px 110px 110px 100px',
                    cursor: 'pointer',
                    borderColor: selected === i ? '#3b82f6' : undefined,
                  }}
                  onClick={() => setSelected(selected === i ? null : i)}
                >
                  <div className="ticker">{o.ticker}</div>
                  <div>
                    <div className="cell-primary">{formatDate(o.expiration)}</div>
                    <div className="cell-secondary">{o.dte} DTE</div>
                  </div>
                  <div><div className="cell-primary">${o.strike.toFixed(2)}</div></div>
                  <div><div className="cell-primary">{o.delta.toFixed(2)}</div></div>
                  <div><div className="cell-primary">{o.iv.toFixed(1)}</div></div>
                  <div><div className="premium">{formatPct(o.weeklyYield)}</div></div>
                  <div>
                    <div className="cell-primary">${o.fillAdjustedPremium.toFixed(2)}</div>
                    <div className="cell-secondary">bid ${o.bid} / ask ${o.ask}</div>
                  </div>
                  <div>{regimeBadge(o.regimeFlag)}</div>
                  <div><span className="view-link">Analyze →</span></div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {selected !== null && results && ruleSet && (
        <DecisionPanel
          opportunity={results.opportunities[selected]}
          ruleSet={ruleSet}
          onClose={() => setSelected(null)}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .scanner-empty { text-align: center; padding: 60px 0; }
        .scanner-empty-icon { font-size: 32px; margin-bottom: 16px; display: block; color: #475569; }
        .scanner-empty p { font-size: 14px; color: #94a3b8; margin-bottom: 8px; }
        .scanner-empty strong { color: #e2e8f0; }
        .scanner-empty-sub { font-size: 12px; color: #475569; }
        .scanner-meta { margin-bottom: 16px; }
        .scanner-summary { font-size: 12px; color: #64748b; display: flex; align-items: center; gap: 8px; }
        .scanner-summary-sep { color: #334155; }
        .no-action-banner { background: #1e2533; border: 0.5px solid #2d3748; border-radius: 10px; padding: 20px 24px; }
        .no-action-title { font-size: 14px; font-weight: 500; color: #f1f5f9; margin-bottom: 6px; }
        .no-action-reason { font-size: 13px; color: #94a3b8; }
      `}</style>
    </>
  );
}
